import { t } from 'i18next'

import type { SectionItemProps } from '@/components/ui/section'
import type { TFormaDePagamentoEnum, TTipoAmortizacaoEnum } from '@/types/simulacao'
import { StringUtils } from '@/utils'
import { formatDateToPtBr } from '@/utils/date.utils'

export type TEmpreendimentoSectionItem = {
  value: string
} & SectionItemProps

type TEmpreendimentoDetailInput = {
  tipoDeAmortizacao?: TTipoAmortizacaoEnum | null
  formaDePagamento?: TFormaDePagamentoEnum | null
  prazoEmMeses: number
  bloco: { previsaoDeEntrega: string; nome: string }
  produto: { tipoDoProduto: string; dataMarcacaoAlvenaria: string | null; codigo: string; metragemPrivativa: number }
  empreendimento: { nome: string }
}

type TCreateEmpreendimentoSectionItemsOutput = {
  empreendimentoItems: TEmpreendimentoSectionItem[]
  detailItems: TEmpreendimentoSectionItem[]
}

export function createEmpreendimentoSectionItems(
  simulacao: TEmpreendimentoDetailInput,
): TCreateEmpreendimentoSectionItemsOutput {
  const prazoLabel = t('simulacao:pages.simulacao.empreendimento.prazo')
  const prazoValue = `${simulacao.prazoEmMeses} ${StringUtils.singularPlural(simulacao.prazoEmMeses, 'mês', 'meses')}`
  const metragemLabel = t('simulacao:pages.simulacao.empreendimento.metragem')
  const metragemValue = t('simulacao:pages.simulacao.empreendimento.metro_quadrado', {
    metragem: simulacao?.produto.metragemPrivativa,
  })

  return {
    empreendimentoItems: createEmpreendimentoItems(simulacao, prazoLabel, prazoValue, metragemLabel, metragemValue),
    detailItems: createDetailItems(simulacao, prazoLabel, prazoValue, metragemLabel, metragemValue),
  }
}

function createEmpreendimentoItems(
  simulacao: TEmpreendimentoDetailInput,
  prazoLabel: string,
  prazoValue: string,
  metragemLabel: string,
  metragemValue: string,
): TEmpreendimentoSectionItem[] {
  const blocoProdutoMap: Record<string, { bloco: string; tipoDoProduto: string }> = {
    Lote: {
      bloco: t('simulacao:pages.simulacao.empreendimento.quadra'),
      tipoDoProduto: t('simulacao:pages.simulacao.empreendimento.lote'),
    },
    Unidade: {
      bloco: t('simulacao:pages.simulacao.empreendimento.torre'),
      tipoDoProduto: t('simulacao:pages.simulacao.empreendimento.unidade'),
    },
    Acessorio: {
      bloco: t('simulacao:pages.simulacao.empreendimento.torre'),
      tipoDoProduto: t('simulacao:pages.simulacao.empreendimento.acessorio'),
    },
  }

  const lastSectionItem = (() => {
    switch (simulacao.tipoDeAmortizacao) {
      case 'SacocH':
      case 'Price':
        return { label: prazoLabel, value: prazoValue }
      case 'SacocV':
      default:
        return { label: metragemLabel, value: metragemValue }
    }
  })()

  return [
    {
      label: t('simulacao:pages.simulacao.empreendimento.nome_empreendimento'),
      value: simulacao.empreendimento.nome,
      colSpan: 4,
      withRightBorder: true,
    },
    {
      label: t('simulacao:pages.simulacao.empreendimento.bloco_produto', {
        bloco: blocoProdutoMap[simulacao.produto.tipoDoProduto]!.bloco,
        tipoDoProduto: blocoProdutoMap[simulacao.produto.tipoDoProduto]!.tipoDoProduto,
      }),
      value: `${simulacao.bloco.nome} - ${simulacao.produto.codigo}`,
      colSpan: 4,
      withRightBorder: true,
    },
    {
      label: lastSectionItem.label,
      value: lastSectionItem.value,
      colSpan: 4,
      withRightBorder: false,
    },
  ]
}

function createDetailItems(
  simulacao: TEmpreendimentoDetailInput,
  prazoLabel: string,
  prazoValue: string,
  metragemLabel: string,
  metragemValue: string,
): TEmpreendimentoSectionItem[] {
  const dataEntregaLabel = t('simulacao:pages.simulacao.empreendimento.data_entrega')
  const dataEntregaValue = simulacao.bloco.previsaoDeEntrega ? formatDateToPtBr(simulacao.bloco.previsaoDeEntrega) : '-'

  switch (simulacao.tipoDeAmortizacao) {
    case 'SacocH':
      return [
        { label: metragemLabel, value: metragemValue, colSpan: 4, withRightBorder: true },
        { label: dataEntregaLabel, value: dataEntregaValue, colSpan: 4, withRightBorder: true },
        {
          label: t('simulacao:pages.simulacao.empreendimento.tipo_amortizacao'),
          value: 'SACOC',
          colSpan: 4,
          withRightBorder: false,
        },
      ]
    case 'Price':
      return [
        { label: metragemLabel, value: metragemValue, colSpan: 4, withRightBorder: true },
        { label: dataEntregaLabel, value: dataEntregaValue, colSpan: 4, withRightBorder: true },
        {
          label: t('simulacao:pages.simulacao.empreendimento.tipo_amortizacao'),
          value: 'PRICE',
          colSpan: 4,
          withRightBorder: false,
        },
      ]
    case 'SacocV':
      return [
        { label: prazoLabel, value: prazoValue, colSpan: 4, withRightBorder: true },
        { label: dataEntregaLabel, value: dataEntregaValue, colSpan: 4, withRightBorder: true },
        {
          label: t('simulacao:pages.simulacao.empreendimento.forma_pagamento'),
          value: simulacao.formaDePagamento ?? '-',
          colSpan: 4,
          withRightBorder: false,
        },
      ]
    default:
      return [
        {
          label: t('simulacao:pages.simulacao.empreendimento.marcacao_alvenaria'),
          value: simulacao.produto.dataMarcacaoAlvenaria
            ? formatDateToPtBr(simulacao.produto.dataMarcacaoAlvenaria)
            : '-',
          colSpan: 4,
          withRightBorder: true,
        },
        { label: prazoLabel, value: prazoValue, colSpan: 4, withRightBorder: true },
        { label: dataEntregaLabel, value: dataEntregaValue, colSpan: 4, withRightBorder: true },
      ]
  }
}
